import * as React from 'react';
// Import form hooks.
import {
	useFormCoverageLimitsAndDeductiblesPersonal,
	UseFormCoverageLimitsAndDeductiblesPersonalResults,
	useFormDwellingInfo,
	UseFormDwellingInfoResults,
	useFormPropertyAddress,
	UseFormPropertyAddressResults,
} from '@app/hooks/view';
// Import props.
import {
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
	ComparativeDwellingSummaryProps,
	IconButtonProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	EntityInstanceType,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	AddressLocationAsMailingAddress,
	AddressLocationWithAccuracy,
	AddressVerification,
	BuildingLimitsPersonal,
	BuildingLimitsPersonalWithRcv,
	BuildingRiskDataPersonal,
	GpsCoordinatesWithAccuracy,
	PerilDeductible,
	ThirdPartyPropertyDataCommercial,
	ThirdPartyPropertyDataPersonal
} from '@app/models/api';
// Import interfaces.
import {
	IAddress,
} from '@app/interfaces';
// Import mui components.
import {
	EditOutlined,
} from '@mui/icons-material';

interface UseComparativeDwellingSummaryParameters {
	props: ComparativeDwellingSummaryProps;
}

interface UseComparativeDwellingSummaryResults {
	oldFormPropertyAddress: UseFormPropertyAddressResults;
	oldFormDwellingInfo: UseFormDwellingInfoResults;
	oldFormCoverageLimits: UseFormCoverageLimitsAndDeductiblesPersonalResults;
	newFormPropertyAddress: UseFormPropertyAddressResults;
	newFormDwellingInfo: UseFormDwellingInfoResults;
	newFormCoverageLimits: UseFormCoverageLimitsAndDeductiblesPersonalResults;
	applyDwellingAddressComparativeDataRowProps: () => ComparativeDataRowProps<IAddress>;
	applyDwellingAddressComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyDwellingAddressErrorStatusComparativeDataHeaderProps: () => ComparativeDataHeaderProps | undefined;
	applyDwellingDetailsComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyDwellingDetailsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyDwellingDetailsErrorStatusComparativeDataHeaderProps: () => ComparativeDataHeaderProps | undefined;
	applyDwellingLimitsComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyDwellingLimitsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyDwellingLimitsErrorStatusComparativeDataHeaderProps: () => ComparativeDataHeaderProps | undefined;
	dwellingAddressBaseId: string;
	dwellingDetailsBaseId: string;
	dwellingLimitsBaseId: string;
}

const useComparativeDwellingSummary = (params: UseComparativeDwellingSummaryParameters): UseComparativeDwellingSummaryResults => {

	// #region exported methods

	// #region dwelling address

	/**
	 * Returns dwelling address comparative data header props. 
	 **/
	const applyDwellingAddressComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${dwellingAddressBaseId}-dwelling-address-header`,
			isSticky: true,
			title: 'Address',
			titleFontSize: 'sm',
			iconButtons: params.props.comparisonDwellingAddressSummary.allowEdits
				? getHeaderEditIconButton(params.props.onEditDwellingAddress, dwellingAddressEditButtonRef)
				: undefined,
			paddingProps: {
				bottom: .5,
				top: .5,
			}
		};
	};

	/**
	 * Returns dwelling address error status comparative data header props. 
	 **/
	const applyDwellingAddressErrorStatusComparativeDataHeaderProps = (): ComparativeDataHeaderProps | undefined => {
		const id: string = `${dwellingAddressBaseId}-dwelling-address-status-header`;
		return getErrorStatusComparativeDataHeaderProps(id, params.props.comparisonDwellingAddressSummary.requiresReview, params.props.comparisonDwellingAddressSummary.allowEdits);
	};

	/**
	 * Returns dwelling address comparative data row props. 
	 **/
	const applyDwellingAddressComparativeDataRowProps = (): ComparativeDataRowProps<IAddress> => {
		return {
			oldValue: oldFormPropertyAddress.subformPropertyAddress.addressGeocoding.addressVerification.addressLocationInput.value.address ?? undefined,
			newValue: newFormPropertyAddress.subformPropertyAddress.addressGeocoding.addressVerification.addressLocationInput.value.address ?? undefined,
			hideNewValueIfSame: true,
			hideDivider: true,
			paddingProps: {
				bottom: .5,
				top: .5,
			}
		};
	};

	// #endregion dwelling address

	// #region dwelling details

	/**
	 * Returns dwelling details comparative data header props. 
	 **/
	const applyDwellingDetailsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${dwellingAddressBaseId}-dwelling-details-header`,
			isSticky: true,
			title: 'Building Details',
			titleFontSize: 'sm',
			iconButtons: params.props.comparisonDwellingDetailsSummary.allowEdits
				? getHeaderEditIconButton(params.props.onEditDwellingDetails, dwellingDetailsEditButtonRef)
				: undefined,
			paddingProps: {
				bottom: .5,
				top: .5,
			}
		};
	};

	/**
	 * Returns dwelling details error status comparative data header props. 
	 **/
	const applyDwellingDetailsErrorStatusComparativeDataHeaderProps = (): ComparativeDataHeaderProps | undefined => {
		const id: string = `${dwellingAddressBaseId}-dwelling-details-status-header`;
		return getErrorStatusComparativeDataHeaderProps(id, params.props.comparisonDwellingDetailsSummary.requiresReview, params.props.comparisonDwellingDetailsSummary.allowEdits);
	};

	/**
	 * Returns dwelling details comparative data row props. 
	 **/
	const applyDwellingDetailsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.buildingUseInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.buildingUseInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.buildingUseInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.occupancyTypeInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.occupancyTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormDwellingInfo.subformDwellingInfo.occupancyTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.constructionTypeInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.constructionTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.constructionTypeInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.foundationType.foundationTypeSelectionListInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.foundationType.foundationTypeDetailSelectionListInput.getSelectedValueDisplayName()?.toTitleCase(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.foundationType.foundationTypeDetailSelectionListInput.getSelectedValueDisplayName()?.toTitleCase(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.belowGradeOrEnclosedAreaFinishedInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.isBelowGradeOrEnclosedAreaFinishedQuestionRequired ? oldFormDwellingInfo.subformDwellingInfo.belowGradeOrEnclosedAreaFinishedInput.getSelectedValueDisplayName()?.toTitleCase() : 'N/A',
				newValue: newFormDwellingInfo.subformDwellingInfo.isBelowGradeOrEnclosedAreaFinishedQuestionRequired ? newFormDwellingInfo.subformDwellingInfo.belowGradeOrEnclosedAreaFinishedInput.getSelectedValueDisplayName()?.toTitleCase() : 'N/A',
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.garageAttachedInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.garageAttachedInput.value !== undefined ? oldFormDwellingInfo.subformDwellingInfo.garageAttachedInput.getSelectedValueDisplayName()?.toTitleCase() : undefined,
				newValue: newFormDwellingInfo.subformDwellingInfo.garageAttachedInput.value !== undefined ? newFormDwellingInfo.subformDwellingInfo.garageAttachedInput.getSelectedValueDisplayName()?.toTitleCase() : undefined,
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.squareFootageInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.squareFootageInput.value?.toString(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.squareFootageInput.value?.toString(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.numberOfStoriesSelectionListInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.numberOfStoriesSelectionListInput.value?.toString(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.numberOfStoriesSelectionListInput.value?.toString(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.yearOfConstructionInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.yearOfConstructionInput.value?.toString(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.yearOfConstructionInput.value?.toString(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.yearRoofLastUpdatedInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.isYearOfLastRoofUpdateQuestionRequired ? oldFormDwellingInfo.subformDwellingInfo.yearRoofLastUpdatedInput.value?.toString() : 'N/A',
				newValue: newFormDwellingInfo.subformDwellingInfo.isYearOfLastRoofUpdateQuestionRequired ? newFormDwellingInfo.subformDwellingInfo.yearRoofLastUpdatedInput.value?.toString() : 'N/A',
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.buildingReplacementCostValueInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.buildingReplacementCostValueInput.getValueAsCurrencyFormat(),
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.buildingReplacementCostValueInput.getValueAsCurrencyFormat(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.designatedFloodZoneInput.label,
				oldValue: oldFormDwellingInfo.subformDwellingInfo.propertyInfo.isTheDesignatedFloodZoneAllowed ? oldFormDwellingInfo.subformDwellingInfo.propertyInfo.designatedFloodZoneInput.value : 'N/A',
				newValue: newFormDwellingInfo.subformDwellingInfo.propertyInfo.isTheDesignatedFloodZoneAllowed ? newFormDwellingInfo.subformDwellingInfo.propertyInfo.designatedFloodZoneInput.value : 'N/A',
				hideNewValueIfSame: true,
			},
		];
	};

	// #endregion dwelling details

	// #region dwelling limits

	/**
	 * Returns dwelling limits comparative data header props. 
	 **/
	const applyDwellingLimitsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${dwellingAddressBaseId}-dwelling-limits-header`,
			isSticky: true,
			title: 'Limits',
			titleFontSize: 'sm',
			iconButtons: params.props.comparisonDwellingLimitsSummary.allowEdits
				? getHeaderEditIconButton(params.props.onEditDwellingLimits, dwellingLimitsEditButtonRef)
				: undefined,
			paddingProps: {
				bottom: .5,
				top: .5,
			}
		};
	};

	/**
	 * Returns dwelling limits error status comparative data header props. 
	 **/
	const applyDwellingLimitsErrorStatusComparativeDataHeaderProps = (): ComparativeDataHeaderProps | undefined => {
		const id: string = `${dwellingAddressBaseId}-dwelling-details-status-header`;
		return getErrorStatusComparativeDataHeaderProps(id, params.props.comparisonDwellingLimitsSummary.requiresReview, params.props.comparisonDwellingLimitsSummary.allowEdits);
	};

	/**
	 * Returns dwelling limits comparative data row props. 
	 **/
	const applyDwellingLimitsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: oldFormCoverageLimits.coverageLimitsPersonalSubform.buildingLimitInput.label,
				oldValue: oldFormCoverageLimits.coverageLimitsPersonalSubform.buildingLimitInput.getValueAsCurrencyFormat(),
				newValue: newFormCoverageLimits.coverageLimitsPersonalSubform.buildingLimitInput.getValueAsCurrencyFormat(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormCoverageLimits.coverageLimitsPersonalSubform.contentsLimitInput.label,
				oldValue: oldFormCoverageLimits.coverageLimitsPersonalSubform.contentsLimitInput.getValueAsCurrencyFormat(),
				newValue: newFormCoverageLimits.coverageLimitsPersonalSubform.contentsLimitInput.getValueAsCurrencyFormat(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormCoverageLimits.coverageLimitsPersonalSubform.personalPropertyLossAdjustmentBasesInput.label,
				oldValue: oldFormCoverageLimits.coverageLimitsPersonalSubform.personalPropertyLossAdjustmentBasesInput.getSelectedValueDisplayName(),
				newValue: newFormCoverageLimits.coverageLimitsPersonalSubform.personalPropertyLossAdjustmentBasesInput.getSelectedValueDisplayName(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormCoverageLimits.coverageLimitsPersonalSubform.otherStructuresLimitInput.label,
				oldValue: oldFormCoverageLimits.coverageLimitsPersonalSubform.otherStructuresLimitInput.getValueAsCurrencyFormat(),
				newValue: newFormCoverageLimits.coverageLimitsPersonalSubform.otherStructuresLimitInput.getValueAsCurrencyFormat(),
				hideNewValueIfSame: true,
			},
			{
				fieldLabel: oldFormCoverageLimits.coverageLimitsPersonalSubform.additionalLivingExpenseLimitInput.label,
				oldValue: oldFormCoverageLimits.coverageLimitsPersonalSubform.additionalLivingExpenseLimitInput.getValueAsCurrencyFormat(),
				newValue: newFormCoverageLimits.coverageLimitsPersonalSubform.additionalLivingExpenseLimitInput.getValueAsCurrencyFormat(),
				hideNewValueIfSame: true,
			},
		];
	};

	// #endregion dwelling limits

	// #endregion exported methods


	// #region private methods

	/**
	 * Generates id for the property address form.
	 */
	const generateIdForFormPropertyAddress = (instanceType: EntityInstanceType): string => {
		return `${dwellingAddressBaseId}-${instanceType}-form`;
	}

	/**
	 * Generates id for the form dwelling info.
	 */
	const generateIdForFormDwellingInfo = (instanceType: EntityInstanceType): string => {
		return `${dwellingDetailsBaseId}-${instanceType}-form`;
	}

	/**
	 * Generates id for the coverage limits form.
	 */
	const generateIdForCoverageLimitsForm = (instanceType: EntityInstanceType): string => {
		return `${dwellingLimitsBaseId}-${instanceType}-form`;
	}

	/**
	 * Creates and address location data instance.
	 */
	const createAddressLocationData = (address: AddressLocationWithAccuracy | undefined): AddressLocationAsMailingAddress => {
		let addressLocation: AddressLocationAsMailingAddress = new AddressLocationAsMailingAddress();
		addressLocation.address = address?.address ?? null;
		addressLocation.gpsCoordinates = address?.gpsCoordinates ?? null;
		addressLocation.copyToMailingAddress = false;

		return addressLocation;
	}

	/**
	 * Creates and building limits with rcv data instance.
	 */
	const createBuildingLimitsWithRcvData = (buildingRiskData: BuildingRiskDataPersonal | undefined, buildingLimits: BuildingLimitsPersonal | undefined): BuildingLimitsPersonalWithRcv => {
		let buildingLimitsWithRcv: BuildingLimitsPersonalWithRcv = new BuildingLimitsPersonalWithRcv();
		buildingLimitsWithRcv.buildingReplacementCostValue = buildingRiskData?.buildingReplacementCostValue ?? null;
		buildingLimitsWithRcv.building = buildingLimits?.building ?? null;
		buildingLimitsWithRcv.contents = buildingLimits?.contents ?? null;
		buildingLimitsWithRcv.otherStructures = buildingLimits?.otherStructures ?? null;
		buildingLimitsWithRcv.additionalLivingExpense = buildingLimits?.additionalLivingExpense ?? null;
		buildingLimitsWithRcv.contentsUseReplacementCostValue = buildingLimits?.contentsUseReplacementCostValue ?? null;

		return buildingLimitsWithRcv;
	}

	/**
	 * Returns dwelling limits error status comparative data header props. 
	 **/
	const getErrorStatusComparativeDataHeaderProps = (id: string, requiresReview: boolean, allowEdits: boolean): ComparativeDataHeaderProps | undefined => {
		let entityComparativeErrorStatusDataHeaderProps: ComparativeDataHeaderProps | undefined = undefined;

		if (requiresReview && allowEdits) {
			entityComparativeErrorStatusDataHeaderProps = {
				id: id,
				isSticky: true,
				title: 'Incomplete - Additional Information Required',
				titleColor: AppThemeColor.ERROR,
				titleFontSize: 'xxsm',
				titleFontStyle: 'italic',
				titleFontWeight: 'normal',
			};
		}

		return entityComparativeErrorStatusDataHeaderProps;
	};

	/**
	 * Fetches the header edit icon button.
	 */
	const getHeaderEditIconButton = (onEdit: () => void, ref: React.RefObject<HTMLButtonElement>): IconButtonProps[] => 
		[{
			label: 'Edit',
			onClick: () => onEdit(),
			icon: EditOutlined,
			ref: ref,
		}]
	

	// #endregion private methods


	// #region variables

	const focusToErrorOnMount: boolean = React.useMemo(() => params.props.focusToErrorOnMount ?? false, [params.props.focusToErrorOnMount]);

	const dwellingAddressEditButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);
	const dwellingDetailsEditButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);
	const dwellingLimitsEditButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);

	const dwellingAddressBaseId: string = React.useMemo(() => {
		return `${params.props.id}-dwellingAddress`;
	}, [params.props.id]);

	const dwellingDetailsBaseId: string = React.useMemo(() => {
		return `${params.props.id}-dwellingDetails`;
	}, [params.props.id]);

	const dwellingLimitsBaseId: string = React.useMemo(() => {
		return `${params.props.id}-dwellingLimits`;
	}, [params.props.id]);

	const oldPropertyAddress: AddressLocationAsMailingAddress = React.useMemo(
		() => createAddressLocationData(params.props.comparisonDwellingAddressSummary.oldData ?? undefined),
		[params.props.comparisonDwellingAddressSummary.oldData],
	);
	const newPropertyAddress: AddressLocationAsMailingAddress = React.useMemo(
		() => createAddressLocationData(params.props.comparisonDwellingAddressSummary.newData ?? undefined),
		[params.props.comparisonDwellingAddressSummary.newData],
	);

	const oldBuildingLimitsWithRcv: BuildingLimitsPersonalWithRcv = React.useMemo(
		() => createBuildingLimitsWithRcvData(params.props.comparisonDwellingDetailsSummary.oldData ?? undefined, params.props.comparisonDwellingLimitsSummary.oldData ?? undefined),
		[params.props.comparisonDwellingDetailsSummary.oldData, params.props.comparisonDwellingLimitsSummary.oldData],
	);
	const newBuildingLimitsWithRcv: BuildingLimitsPersonalWithRcv = React.useMemo(
		() => createBuildingLimitsWithRcvData(params.props.comparisonDwellingDetailsSummary.newData ?? undefined, params.props.comparisonDwellingLimitsSummary.newData ?? undefined),
		[params.props.comparisonDwellingDetailsSummary.newData, params.props.comparisonDwellingLimitsSummary.newData],
	);

	// Old form property address.
	const oldFormPropertyAddress: UseFormPropertyAddressResults = useFormPropertyAddress({
		id: generateIdForFormPropertyAddress(EntityInstanceType.OLD),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			propertyAddress: oldPropertyAddress,
			possibleStates: params.props.possibleStates,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			addressProps: {
				geocodeAddress: async (address: Address) => new GpsCoordinatesWithAccuracy(),
				getAddressSuggestions: async (addressQuery: string) => [],
				verifyAddress: async (address: Address) => new AddressVerification(),
				verifySingleLineAddress: async (singleLineAddress: string) => new AddressVerification(),
				getThirdPartyPropertyDataCommercial: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataCommercial(),
				getThirdPartyPropertyDataPersonal: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataPersonal(),
			},
			onSavePropertyAddress: async (address: AddressLocationAsMailingAddress) => { },
			showValidationsOnMount: true,
		},
	});

	// Old form dwelling info.
	const oldFormDwellingInfo: UseFormDwellingInfoResults = useFormDwellingInfo({
		id: generateIdForFormDwellingInfo(EntityInstanceType.OLD),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			propertyDomainValuesProps: params.props.propertyDomainValuesProps,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			hasEarthquakePeril: params.props.comparisonDwellingDetailsSummary.oldHasEarthquakePerilSelected,
			hasFloodPeril: params.props.comparisonDwellingDetailsSummary.oldHasFloodPerilSelected,
			propertyInfo: params.props.comparisonDwellingDetailsSummary.oldData ?? undefined,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			onSaveDwellingInfo: (buildingRiskDataPersonal: BuildingRiskDataPersonal) => { },
			showValidationsOnMount: true,
		},
	});

	// Old form coverage limits.
	const oldFormCoverageLimits: UseFormCoverageLimitsAndDeductiblesPersonalResults = useFormCoverageLimitsAndDeductiblesPersonal({
		id: generateIdForCoverageLimitsForm(EntityInstanceType.OLD),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			deductibleProps: {
				possiblePerils: params.props.possiblePerils,
				selectedPerilDeductibles: params.props.comparisonDwellingLimitsSummary.oldSelectedPerilsDeductibles,
			},
			buildingLimitsPersonal: oldBuildingLimitsWithRcv,
			onCalculatePremium: (buildingLimitsPersonal: BuildingLimitsPersonal, selectedPerilDeductibles: PerilDeductible[]) => { },
			onSaveBuildingLimits: (buildingLimitsPersonal: BuildingLimitsPersonal, selectedPerilDeductibles: PerilDeductible[]) => { },
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			showValidationsOnMount: true,
		},
	});

	// New form property address.
	const newFormPropertyAddress: UseFormPropertyAddressResults = useFormPropertyAddress({
		id: generateIdForFormPropertyAddress(EntityInstanceType.NEW),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			propertyAddress: newPropertyAddress,
			possibleStates: params.props.possibleStates,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			addressProps: {
				geocodeAddress: async (address: Address) => new GpsCoordinatesWithAccuracy(),
				getAddressSuggestions: async (addressQuery: string) => [],
				verifyAddress: async (address: Address) => new AddressVerification(),
				verifySingleLineAddress: async (singleLineAddress: string) => new AddressVerification(),
				getThirdPartyPropertyDataCommercial: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataCommercial(),
				getThirdPartyPropertyDataPersonal: async (address: Address, lat: number, lng: number) => new ThirdPartyPropertyDataPersonal(),
			},
			onSavePropertyAddress: async (address: AddressLocationAsMailingAddress) => { },
			showValidationsOnMount: true,
		},
	});

	// New form dwelling info.
	const newFormDwellingInfo: UseFormDwellingInfoResults = useFormDwellingInfo({
		id: generateIdForFormDwellingInfo(EntityInstanceType.NEW),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			propertyDomainValuesProps: params.props.propertyDomainValuesProps,
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			hasEarthquakePeril: params.props.comparisonDwellingDetailsSummary.newHasEarthquakePerilSelected,
			hasFloodPeril: params.props.comparisonDwellingDetailsSummary.newHasFloodPerilSelected,
			propertyInfo: params.props.comparisonDwellingDetailsSummary.newData ?? undefined,
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			onSaveDwellingInfo: (buildingRiskDataPersonal: BuildingRiskDataPersonal) => { },
			showValidationsOnMount: true,
		},
	});

	// New form coverage limits.
	const newFormCoverageLimits: UseFormCoverageLimitsAndDeductiblesPersonalResults = useFormCoverageLimitsAndDeductiblesPersonal({
		id: generateIdForCoverageLimitsForm(EntityInstanceType.NEW),
		props: {
			isReviewRequired: true,
			allowEdits: true,
			deductibleProps: {
				possiblePerils: params.props.possiblePerils,
				selectedPerilDeductibles: params.props.comparisonDwellingLimitsSummary.oldSelectedPerilsDeductibles,
			},
			buildingLimitsPersonal: newBuildingLimitsWithRcv,
			onCalculatePremium: (buildingLimitsPersonal: BuildingLimitsPersonal, selectedPerilDeductibles: PerilDeductible[]) => { },
			onSaveBuildingLimits: (buildingLimitsPersonal: BuildingLimitsPersonal, selectedPerilDeductibles: PerilDeductible[]) => { },
			confirmationSaveMessage: String.empty(),
			requireConfirmationOnSave: false,
			showValidationsOnMount: true,
		},
	});

	// #endregion private variables

	// #region lifecycle methods.

	React.useEffect(() => {
		if (focusToErrorOnMount) {
			if (params.props.comparisonDwellingAddressSummary.requiresReview
				&& dwellingAddressEditButtonRef.current !== null) {
				dwellingAddressEditButtonRef.current.focus();
			}
			else if (params.props.comparisonDwellingDetailsSummary.requiresReview
				&& dwellingDetailsEditButtonRef.current !== null) {
				dwellingDetailsEditButtonRef.current.focus();
			}
			else if (params.props.comparisonDwellingLimitsSummary.requiresReview
				&& dwellingLimitsEditButtonRef.current !== null) {
				dwellingLimitsEditButtonRef.current.focus();
			}
		}
	}, []);

	// #endregion lifecycle methods.

	return {
		oldFormPropertyAddress,
		oldFormDwellingInfo,
		oldFormCoverageLimits,
		newFormPropertyAddress,
		newFormDwellingInfo,
		newFormCoverageLimits,
		applyDwellingAddressComparativeDataRowProps,
		applyDwellingAddressComparativeDataHeaderProps,
		applyDwellingAddressErrorStatusComparativeDataHeaderProps,
		applyDwellingDetailsComparativeDataRowProps,
		applyDwellingDetailsComparativeDataHeaderProps,
		applyDwellingDetailsErrorStatusComparativeDataHeaderProps,
		applyDwellingLimitsComparativeDataRowProps,
		applyDwellingLimitsComparativeDataHeaderProps,
		applyDwellingLimitsErrorStatusComparativeDataHeaderProps,
		dwellingAddressBaseId,
		dwellingDetailsBaseId,
		dwellingLimitsBaseId,
	};
}

// Export parameters.
export {
	UseComparativeDwellingSummaryParameters,
}

// Export results.
export {
	UseComparativeDwellingSummaryResults,
}

export default useComparativeDwellingSummary;